import { AxiosError } from 'axios';

import type { UnavailableInputs } from '@/clients/v2';

export class ApiError extends AxiosError {
  apiCode?: string;

  constructor(message: string, apiCode?: string) {
    super(message);
    this.name = 'ApiError';
    this.apiCode = apiCode;
  }
}

export class BadRequestError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'BadRequestError';
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

export class PaymentRequiredError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'PaymentRequiredError';
  }
}

export class ForbiddenError extends ApiError {
  constructor(message: string, apiCode?: string) {
    super(message, apiCode);
    this.name = 'ForbiddenError';
  }
}

export class RecentAuthenticationRequiredError extends ForbiddenError {
  constructor(message: string) {
    super(message, 'recent_authentication_required');
    this.name = 'RecentAuthenticationRequiredError';
  }
}

export class NotFoundError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'NotFoundError';
  }
}

export class NotAllowedError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'NotAllowedError';
  }
}

export class NotAcceptableError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'NotAcceptableError';
  }
}

export class PreconditionFailedError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'PreconditionFailedError';
  }
}

export class UnsupportedMediaTypeError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'UnsupportedMediaTypeError';
  }
}

export class InternalServerError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'InternalServerError';
  }
}

export class NotImplementedError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'NotImplementedError';
  }
}

export class ContentTooLargeError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'ContentTooLargeError';
  }
}

export const DOCUMENT_PROCESSING_CONFLICT_CODE = 'document_processing';

export class ConflictError extends ApiError {
  constructor(message: string, apiCode?: string) {
    super(message, apiCode);
    this.name = 'ConflictError';
  }
}

export class UnprocessableContentError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'UnprocessableContentError';
  }
}

export class ConnectionTimeoutError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'ConnectionTimeoutError';
  }
}

export class BadGatewayError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'BadGatewayError';
  }
}

export class ServiceUnavailableError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'ServiceUnavailableError';
  }
}

export class NetworkUnavailableError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkUnavailableError';
  }
}

export class CanceledError extends ApiError {
  constructor(message: string) {
    super(message);
    this.name = 'CanceledError';
  }
}

/** The server refused the request (4xx) and stored nothing; the caller may offer the input again. */
export class RejectedError extends ApiError {
  readonly unavailableInputs: UnavailableInputs | null;

  constructor(
    message: string,
    apiCode?: string,
    unavailableInputs: UnavailableInputs | null = null,
  ) {
    super(message, apiCode);
    this.name = 'RejectedError';
    this.unavailableInputs = unavailableInputs;
  }
}
